import React, { useEffect, useState, useMemo } from 'react'
import {
    Box,
    Typography,
    Collapse,
} from "@mui/material"
import axios from 'axios'
import { toastError, getObject } from '../../utils'
import FronicLogo from '../../assets/img/Fronic_Logo_white.png'
import { Users, Activity, Eye, Globe, User, ChevronDown } from 'lucide-react'

const animations = `
    @keyframes pulse-dot {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.3); opacity: 0.7; }
    }
    @keyframes ripple {
        0% { transform: scale(0.8); opacity: 1; }
        100% { transform: scale(2.8); opacity: 0; }
    }
    @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
    }
    @keyframes barGrow {
        from { width: 0; }
    }
`

const LiveDot = () => (
    <Box sx={{
        position: 'relative',
        width: 10,
        height: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }}>
        {[0, 0.5, 1].map((delay) => (
            <Box key={delay} sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                border: '1.5px solid #EF4444',
                animation: 'ripple 1.5s ease-out infinite',
                animationDelay: `${delay}s`,
            }} />
        ))}
        <Box sx={{
            width: 10,
            height: 10,
            backgroundColor: '#EF4444',
            borderRadius: '50%',
            animation: 'pulse-dot 1.5s ease-in-out infinite',
            boxShadow: '0 0 8px rgba(239, 68, 68, 0.5)',
            zIndex: 1,
        }} />
    </Box>
)

const StatCard = ({ icon, label, value, color, sub }) => (
    <Box sx={{
        background: '#1A1D27',
        border: '1px solid #2E3247',
        borderRadius: '14px',
        p: 2.5,
        flex: 1,
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
            borderColor: color,
            boxShadow: `0 0 20px ${color}15`,
            transform: 'translateY(-2px)',
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
            opacity: 0.6,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                backgroundColor: `${color}18`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: color,
            }}>
                {icon}
            </Box>
            {sub && (
                <Typography sx={{ fontSize: '0.68rem', color: '#64748B', fontWeight: 500, letterSpacing: '0.05em' }}>
                    {sub}
                </Typography>
            )}
        </Box>
        <Typography sx={{
            fontSize: 'clamp(28px, 4vh, 48px)',
            fontWeight: 800,
            color: color,
            lineHeight: 1,
            mb: 0.5,
            textShadow: `0 0 25px ${color}30`,
        }}>
            {value}
        </Typography>
        <Typography sx={{
            fontSize: '0.72rem',
            color: '#64748B',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
        }}>
            {label}
        </Typography>
    </Box>
)

const PageBar = ({ name, count, maxCount, index, users, defaultExpanded }) => {
    const [expanded, setExpanded] = useState(defaultExpanded)
    const pct = maxCount > 0 ? (count / maxCount) * 100 : 0
    const isActive = count > 0
    const hasUsers = users && users.length > 0

    return (
        <Box sx={{
            animation: 'fadeInUp 0.4s ease forwards',
            animationDelay: `${index * 0.04}s`,
            opacity: 0,
            mb: 0.3,
        }}>
            <Box
                onClick={() => hasUsers && setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1,
                    px: 1.5,
                    borderRadius: '10px',
                    cursor: hasUsers ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: hasUsers ? 'rgba(108, 99, 255, 0.05)' : 'transparent',
                    },
                }}
            >
                <Typography sx={{
                    fontSize: '0.72rem',
                    color: '#4A5068',
                    fontWeight: 600,
                    width: 20,
                    textAlign: 'right',
                    flexShrink: 0,
                }}>
                    {index + 1}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0 }}>
                            <Typography sx={{
                                fontSize: '0.8rem',
                                color: isActive ? '#E2E8F0' : '#64748B',
                                fontWeight: isActive ? 600 : 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {name}
                            </Typography>
                            {hasUsers && (
                                <ChevronDown
                                    size={13}
                                    color="#4A5068"
                                    style={{
                                        transition: 'transform 0.2s ease',
                                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                        </Box>
                        <Typography sx={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: isActive ? '#10B981' : '#4A5068',
                            ml: 1.5,
                            flexShrink: 0,
                        }}>
                            {count}
                        </Typography>
                    </Box>
                    <Box sx={{
                        width: '100%',
                        height: 5,
                        backgroundColor: '#1E2130',
                        borderRadius: 3,
                        overflow: 'hidden',
                    }}>
                        <Box sx={{
                            height: '100%',
                            width: `${pct}%`,
                            borderRadius: 3,
                            background: isActive
                                ? 'linear-gradient(90deg, #10B981, #34D399)'
                                : '#2E3247',
                            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                            animation: 'barGrow 0.8s ease forwards',
                            animationDelay: `${index * 0.04}s`,
                            boxShadow: isActive ? '0 0 8px rgba(16, 185, 129, 0.3)' : 'none',
                        }} />
                    </Box>
                </Box>
            </Box>
            {hasUsers && (
                <Collapse in={expanded} timeout={200}>
                    <Box sx={{ pl: 5.5, pr: 1.5, pb: 0.5 }}>
                        {users.map((user, i) => (
                            <Box key={i} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 0.6,
                                px: 1.2,
                                borderRadius: '6px',
                                borderLeft: '2px solid #2E3247',
                                ml: 1,
                                mb: 0.2,
                                '&:hover': { backgroundColor: 'rgba(108, 99, 255, 0.04)' },
                            }}>
                                <Box sx={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: '50%',
                                    backgroundColor: '#10B981',
                                    boxShadow: '0 0 4px rgba(16, 185, 129, 0.4)',
                                    flexShrink: 0,
                                }} />
                                <Typography sx={{ fontSize: '0.72rem', color: '#E2E8F0', fontWeight: 500 }}>
                                    {user.name || 'Unknown'}
                                </Typography>
                                {user.email && (
                                    <Typography sx={{ fontSize: '0.65rem', color: '#4A5068' }}>
                                        {user.email}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    )
}

const UserRow = ({ user, index }) => {
    const getInitials = (name) => {
        if (!name) return '?'
        return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    }

    const displayName = user.name || user.label_name || user.email || 'Unknown'
    const currentPage = user.current_page || user.page || ''

    const colors = ['#6C63FF', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#8B5CF6', '#06B6D4']
    const color = colors[index % colors.length]

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1,
            px: 1.5,
            borderRadius: '10px',
            animation: 'fadeInUp 0.3s ease forwards',
            animationDelay: `${index * 0.03}s`,
            opacity: 0,
            transition: 'background-color 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(108, 99, 255, 0.06)',
            },
        }}>
            <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: `${color}20`,
                border: `1px solid ${color}40`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: color }}>
                    {getInitials(displayName)}
                </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                    fontSize: '0.78rem',
                    color: '#E2E8F0',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {displayName}
                </Typography>
                {currentPage && (
                    <Typography sx={{
                        fontSize: '0.65rem',
                        color: '#4A5068',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                        {currentPage}
                    </Typography>
                )}
            </Box>
            <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#10B981',
                boxShadow: '0 0 6px rgba(16, 185, 129, 0.5)',
                flexShrink: 0,
            }} />
        </Box>
    )
}

export default function LiveAnalytics() {
    const [analyticsData, setAnalyticsData] = useState({
        total_online_users: 0,
        online_users_list: [],
        pages: []
    })
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        document.title = 'LA | Live Analytics';
        return () => { document.title = 'Fronic Media Admin'; };
    }, []);

    useEffect(() => {
        document.body.style.margin = '0'
        document.body.style.padding = '0'
        document.body.style.overflow = 'hidden'

        fetchAnalytics()
        const interval = setInterval(() => {
            fetchAnalytics()
        }, 30000)

        return () => {
            document.body.style.margin = ''
            document.body.style.padding = ''
            document.body.style.overflow = ''
            clearInterval(interval)
        }
    }, [])

    const fetchAnalytics = () => {
        setLoading(true)
        const auth = getObject("auth")

        axios.get('https://backend.fronicmedia.com/api/getOnlineUsersAdmin', {
            headers: {
                Authorization: auth
            }
        })
            .then(response => {
                if (response.data.status === 200) {
                    setAnalyticsData(response.data.body)
                }
                setLoading(false)
            })
            .catch(err => {
                toastError(err?.message || 'Failed to fetch analytics');
                setLoading(false)
            })
    }

    const sortedPages = useMemo(() =>
        [...(analyticsData.pages || [])].sort((a, b) => b.active_users_count - a.active_users_count),
        [analyticsData.pages]
    )

    const maxPageCount = useMemo(() =>
        Math.max(...(sortedPages.map(p => p.active_users_count) || [0]), 1),
        [sortedPages]
    )

    const activePages = useMemo(() =>
        sortedPages.filter(p => p.active_users_count > 0).length,
        [sortedPages]
    )

    const usersList = analyticsData.online_users_list || []

    return (
        <>
            <style>{animations}</style>

            <Box sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                height: '100vh',
                width: '100vw',
                background: '#0F1117',
                color: '#fff',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                p: '1.2% 1.5%',
            }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                    flexShrink: 0,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <img
                            src={FronicLogo}
                            alt="Fronic Media"
                            style={{
                                height: 'clamp(24px, 4vh, 44px)',
                                width: 'auto',
                                objectFit: 'contain',
                            }}
                        />
                        <Box sx={{
                            height: 24,
                            width: '1px',
                            backgroundColor: '#2E3247',
                        }} />
                        <Typography sx={{
                            fontSize: 'clamp(16px, 2.2vh, 28px)',
                            fontWeight: 700,
                            color: '#F1F5F9',
                            letterSpacing: '0.02em',
                        }}>
                            Live Analytics
                        </Typography>
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        backgroundColor: 'rgba(239, 68, 68, 0.08)',
                        border: '1px solid rgba(239, 68, 68, 0.15)',
                        borderRadius: '10px',
                        px: 2,
                        py: 0.8,
                    }}>
                        <Typography sx={{
                            fontSize: '0.75rem',
                            color: '#94A3B8',
                            fontWeight: 500,
                        }}>
                            LIVE
                        </Typography>
                        <LiveDot />
                    </Box>
                </Box>

                {/* Stat Cards Row */}
                <Box sx={{
                    display: 'flex',
                    gap: 1.5,
                    mb: 1.5,
                    flexShrink: 0,
                }}>
                    <StatCard
                        icon={<Users size={18} />}
                        label="Users Online"
                        value={analyticsData.total_online_users}
                        color="#10B981"
                        sub="REAL-TIME"
                    />
                    <StatCard
                        icon={<Activity size={18} />}
                        label="Active Pages"
                        value={activePages}
                        color="#6C63FF"
                        sub={`OF ${sortedPages.length}`}
                    />
                    <StatCard
                        icon={<Globe size={18} />}
                        label="Total Pages"
                        value={sortedPages.length}
                        color="#F59E0B"
                        sub="TRACKED"
                    />
                    <StatCard
                        icon={<Eye size={18} />}
                        label="Peak Page"
                        value={maxPageCount}
                        color="#EC4899"
                        sub={sortedPages[0]?.page_name?.slice(0, 16) || '—'}
                    />
                </Box>

                {/* Main Content: Page Activity + Users List */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    gap: 1.5,
                    minHeight: 0,
                    overflow: 'hidden',
                }}>
                    {/* Page Activity Panel */}
                    <Box sx={{
                        flex: 3,
                        background: '#1A1D27',
                        border: '1px solid #2E3247',
                        borderRadius: '14px',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2.5,
                            py: 1.8,
                            borderBottom: '1px solid #2E3247',
                            flexShrink: 0,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Activity size={16} color="#6C63FF" />
                                <Typography sx={{
                                    fontSize: '0.82rem',
                                    fontWeight: 700,
                                    color: '#E2E8F0',
                                    letterSpacing: '0.02em',
                                }}>
                                    Page Activity
                                </Typography>
                            </Box>
                            <Typography sx={{
                                fontSize: '0.68rem',
                                color: '#4A5068',
                                fontWeight: 500,
                            }}>
                                {activePages} active / {sortedPages.length} total
                            </Typography>
                        </Box>
                        <Box sx={{
                            flex: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            py: 0.5,
                            px: 1,
                            '&::-webkit-scrollbar': { width: '3px' },
                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#2E3247',
                                borderRadius: '3px',
                            },
                        }}>
                            {sortedPages.length > 0 ? (
                                sortedPages.map((page, index) => (
                                    <PageBar
                                        key={index}
                                        name={page.page_name}
                                        count={page.active_users_count}
                                        maxCount={maxPageCount}
                                        index={index}
                                        users={page.users}
                                        defaultExpanded={index === 0}
                                    />
                                ))
                            ) : (
                                <Box sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                }}>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#4A5068' }}>
                                        No page data available
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Online Users Panel */}
                    <Box sx={{
                        flex: 2,
                        background: '#1A1D27',
                        border: '1px solid #2E3247',
                        borderRadius: '14px',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2.5,
                            py: 1.8,
                            borderBottom: '1px solid #2E3247',
                            flexShrink: 0,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <User size={16} color="#10B981" />
                                <Typography sx={{
                                    fontSize: '0.82rem',
                                    fontWeight: 700,
                                    color: '#E2E8F0',
                                    letterSpacing: '0.02em',
                                }}>
                                    Online Users
                                </Typography>
                            </Box>
                            <Box sx={{
                                backgroundColor: 'rgba(16, 185, 129, 0.12)',
                                borderRadius: '6px',
                                px: 1,
                                py: 0.3,
                            }}>
                                <Typography sx={{
                                    fontSize: '0.68rem',
                                    color: '#10B981',
                                    fontWeight: 700,
                                }}>
                                    {usersList.length}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{
                            flex: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            py: 0.5,
                            px: 1,
                            '&::-webkit-scrollbar': { width: '3px' },
                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#2E3247',
                                borderRadius: '3px',
                            },
                        }}>
                            {usersList.length > 0 ? (
                                usersList.map((user, index) => (
                                    <UserRow key={index} user={user} index={index} />
                                ))
                            ) : (
                                <Box sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                }}>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#4A5068' }}>
                                        No users online
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    )
}
